import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Lock, Plane, Heart, Shield, ArrowRight, Clock, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import ErrorDisplay from '@/components/ErrorDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRY_ICONS = {
  'Defense': Plane,
  'Aerospace': Plane,
  'Healthcare': Heart,
  'Manufacturing': Shield,
  'Industrial': Shield,
  'All': FileText
};

const TIER_COLORS = {
  'pilot': 'bg-gray-100 text-gray-700',
  'pro': 'bg-blue-100 text-blue-700',
  'enterprise': 'bg-purple-100 text-purple-700',
  'defense': 'bg-red-100 text-red-700'
};

export default function PlaybookList({ user, logout }) {
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState({ available: [], locked: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startingPlaybook, setStartingPlaybook] = useState(null);

  useEffect(() => {
    fetchPlaybooks();
  }, []);

  const fetchPlaybooks = async () => {
    try {
      const token = localStorage.getItem('standari_token');
      const response = await axios.get(`${BACKEND_URL}/api/playbooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaybooks(response.data);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  const startWorkflow = async (playbookId) => {
    try {
      setStartingPlaybook(playbookId);
      const token = localStorage.getItem('standari_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/playbooks/${playbookId}/start`,
        { assigned_users: {}, metadata: {} },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Navigate to workflow
      navigate(`/workflows/${response.data.workflow_id}`);
    } catch (err) {
      setError(err);
      setStartingPlaybook(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading playbooks...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Workflow Playbooks</h1>
              <p className="text-sm text-gray-600 mt-1">
                Guided workflows that turn standards into steps anyone can follow
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Badge className={TIER_COLORS[playbooks.user_tier || 'pilot']}>
                {(playbooks.user_tier || 'pilot').toUpperCase()} Tier
              </Badge>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6">
            <ErrorDisplay error={error} onRetry={fetchPlaybooks} />
          </div>
        )}

        {/* Available Playbooks */}
        {playbooks.available.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Available in Your Tier</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {playbooks.available.map(playbook => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  locked={false}
                  onStart={() => startWorkflow(playbook.id)}
                  onViewDetails={() => navigate(`/playbooks/${playbook.id}`)}
                  isStarting={startingPlaybook === playbook.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* Locked Playbooks */}
        {playbooks.locked.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="h-5 w-5 mr-2" />
              Unlock with Higher Tier
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {playbooks.locked.map(playbook => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  locked={true}
                  onViewDetails={() => navigate(`/playbooks/${playbook.id}`)}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function PlaybookCard({ playbook, locked, onStart, onViewDetails, isStarting }) {
  const Icon = INDUSTRY_ICONS[playbook.industry[0]] || FileText;
  
  return (
    <Card className={`${locked ? 'opacity-60 border-dashed' : 'hover:shadow-lg'} transition-all`}>
      <CardHeader>
        <div className="flex items-start justify-between mb-3">
          <div className={`p-3 rounded-lg ${locked ? 'bg-gray-100' : 'bg-blue-100'}`}>
            <Icon className={`h-6 w-6 ${locked ? 'text-gray-400' : 'text-blue-600'}`} />
          </div>
          <Badge className={TIER_COLORS[playbook.tier_required]}>
            {playbook.tier_required.toUpperCase()}
          </Badge>
        </div>
        <CardTitle className="text-lg">
          {locked && <Lock className="h-4 w-4 inline mr-2" />}
          {playbook.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {playbook.description}
        </p>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-xs text-gray-500">
            <span className="font-medium mr-2">Industry:</span>
            {playbook.industry.join(', ')}
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <span className="font-medium mr-2">Standards:</span>
            {playbook.standards_required.length}
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <Clock className="h-3 w-3 mr-1" />
            {playbook.steps.length} steps
          </div>
        </div>

        {locked ? (
          <div className="space-y-2">
            <Button 
              onClick={onViewDetails}
              variant="outline" 
              className="w-full"
            >
              View Details
            </Button>
            <p className="text-xs text-center text-gray-500">
              Requires {playbook.tier_required} tier
            </p>
          </div>
        ) : (
          <div className="flex space-x-2">
            <Button 
              onClick={onViewDetails}
              variant="outline"
              className="flex-1"
            >
              Details
            </Button>
            <Button 
              onClick={onStart}
              disabled={isStarting}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isStarting ? 'Starting...' : 'Start'}
              {!isStarting && <ArrowRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
